import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { dateTime, humanise, num } from '../lib/format';
import { DataTable, useListParams } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { Badge, Button, Card, CardHead, Chips, Field, Modal, Notice, SearchInput, Select, Textarea, useToast } from '../components/ui';
import type { BadgeTone } from '../components/ui';

type Status = 'DRAFT' | 'KYC_REQUIRED' | 'PENDING_VERIFICATION' | 'CHANGES_REQUIRED' | 'VERIFIED' | 'REJECTED' | 'GRACE_EXPIRED';

interface Summary {
  submissions: number;
  pending: number;
  identityPending: number;
  approvedUsers: number;
  rejected: number;
  approaching: number;
  expired: number;
  awaitingActivation: number;
}

interface AccessRow {
  id: string;
  sector: string;
  status: Status;
  rule: 'GRACE' | 'APPROVAL';
  activatedAt: string;
  graceEndsAt: string | null;
  daysLeft: number | null;
  submittedAt: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  user: { id: string; displayName: string | null; mobile: string; kycStatus: string };
}

interface Detail extends Omit<AccessRow, 'user'> {
  user: { id: string; displayName: string | null; mobile: string; email: string | null; kycStatus: string; createdAt: string };
  identity: Array<{ id: string; docType: string; docLast4: string | null; nameOnDoc: string | null; status: string; vendorName: string; createdAt: string }>;
  provider: null | {
    name: string;
    businessName: string | null;
    address: string;
    workDescription: string;
    whatsappMobile: string | null;
    coverageArea: string | null;
    experienceYears: number | null;
    qualifications: string | null;
    pricing: string | null;
    teamSize: number;
    isLive: boolean;
    customService: string | null;
    workMedia: string[];
    category: { name: string };
  };
  documents: Array<{ id: string; kind: string; fileName: string; mimeType: string; createdAt: string; url: string }>;
  history: Array<{ id: string; action: string; createdAt: string; after: { note?: string | null } | null; actor: { displayName: string | null } | null }>;
  listings: Record<string, number>;
}

export const STATUS_LABEL: Record<Status, string> = {
  DRAFT: 'Draft',
  KYC_REQUIRED: 'KYC required',
  PENDING_VERIFICATION: 'Pending verification',
  CHANGES_REQUIRED: 'Changes required',
  VERIFIED: 'Verified',
  REJECTED: 'Rejected',
  GRACE_EXPIRED: 'Grace period expired',
};

const STATUS_TONE: Record<Status, BadgeTone> = {
  DRAFT: 'neutral',
  KYC_REQUIRED: 'blue',
  PENDING_VERIFICATION: 'amber',
  CHANGES_REQUIRED: 'orange',
  VERIFIED: 'green',
  REJECTED: 'red',
  GRACE_EXPIRED: 'red',
};

const SECTORS = [
  { value: '', label: 'All sections' },
  { value: 'REAL_ESTATE', label: 'Real Estate' },
  { value: 'BUY_SELL', label: 'Buy & Sell' },
  { value: 'HOME_SERVICE', label: 'Home Services' },
  { value: 'LOCAL_SERVICE', label: 'Local Services' },
];
const sectorName = (s: string) => SECTORS.find((x) => x.value === s)?.label ?? humanise(s);

function Stat({ label, value, sub, onClick }: { label: string; value: number | undefined; sub?: string; onClick?: () => void }) {
  return (
    <div onClick={onClick} style={{ cursor: onClick ? 'pointer' : undefined }}>
      <Card>
        <div className="stat">
          <div>
            <div className="stat__label">{label}</div>
            <div className="stat__value">{value === undefined ? '—' : num(value)}</div>
            {sub && <div className="stat__delta stat__delta--flat">{sub}</div>}
          </div>
        </div>
      </Card>
    </div>
  );
}

/**
 * KYC by section, in one place: who is inside their 30 days in Real Estate
 * and Buy & Sell and how many days they have, whose time has run out, and
 * the Home and Local Service applications waiting for a decision.
 */
export function KycManagementPage() {
  const { get, set } = useListParams();
  const sector = get('sector');
  const status = get('status');
  const deadline = get('deadline');
  const q = get('q');
  const [openId, setOpenId] = useState<string | null>(null);

  const summary = useQuery({ queryKey: ['kyc-summary'], queryFn: () => api.get<Summary>('/admin/kyc-management/summary') });
  const s = summary.data;
  const show = (params: Record<string, string>) => {
    for (const k of ['sector', 'status', 'deadline']) set(k, params[k] ?? '');
  };

  const columns: Array<Column<AccessRow>> = [
    {
      key: 'who',
      header: 'Person',
      render: (r) => (
        <div>
          <div className="cell-strong">
            <Link to={`/users/${r.user.id}`} onClick={(e) => e.stopPropagation()}>{r.user.displayName ?? 'Unnamed'}</Link>
          </div>
          <div className="cell-muted cell-mono">{r.user.mobile}</div>
        </div>
      ),
    },
    {
      key: 'sector',
      header: 'Section',
      render: (r) => (
        <div>
          <div>{sectorName(r.sector)}</div>
          <div className="cell-muted">{r.rule === 'GRACE' ? '30-day grace' : 'Approval before live'}</div>
        </div>
      ),
    },
    { key: 'status', header: 'Status', render: (r) => <Badge tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Badge> },
    { key: 'identity', header: 'Identity KYC', render: (r) => <span className="cell-muted">{humanise(r.user.kycStatus)}</span> },
    {
      key: 'clock',
      header: 'Deadline / submitted',
      render: (r) =>
        r.rule === 'GRACE' ? (
          <div>
            <div className="cell-muted">Due {dateTime(r.graceEndsAt)}</div>
            {r.status !== 'VERIFIED' && r.daysLeft !== null && (
              <div className={r.daysLeft <= 7 ? 'cell-strong' : 'cell-muted'}>{r.daysLeft === 0 ? 'Ended' : `${r.daysLeft} days left`}</div>
            )}
          </div>
        ) : (
          <span className="cell-muted">{r.submittedAt ? `Submitted ${dateTime(r.submittedAt)}` : 'Not submitted'}</span>
        ),
    },
  ];

  return (
    <div className="stack">
      <div className="grid grid--stats">
        <Stat label="Total submissions" value={s?.submissions} sub={s ? `${num(s.identityPending)} identity checks pending` : undefined} onClick={() => show({})} />
        <Stat label="Pending verification" value={s?.pending} onClick={() => show({ status: 'PENDING_VERIFICATION' })} />
        <Stat label="Approved users" value={s?.approvedUsers} sub="Identity KYC verified" />
        <Stat label="Rejected" value={s?.rejected} onClick={() => show({ status: 'REJECTED' })} />
        <Stat label="Deadline within 7 days" value={s?.approaching} onClick={() => show({ deadline: 'soon' })} />
        <Stat label="Grace period expired" value={s?.expired} onClick={() => show({ deadline: 'expired' })} />
        <Stat label="Services awaiting activation" value={s?.awaitingActivation} onClick={() => show({ status: 'PENDING_VERIFICATION', sector: '' })} />
      </div>

      <Notice tone="info" title="How KYC works by section">
        Real Estate and Buy &amp; Sell open at once; KYC is due within 30 days of a person's first listing in the section, with
        reminders on days 15, 25, 28 and 30. After that their listings are hidden (kept, not deleted) until KYC is approved.
        Home and Local Services are never public until you approve the application here. Identity is checked through
        DigiLocker on the <Link to="/kyc">KYC review</Link> page.
      </Notice>

      <DataTable<AccessRow>
        queryKey={['kyc-management', sector, status, deadline, q]}
        path="/admin/kyc-management"
        params={{ sector, status, deadline, q }}
        columns={columns}
        onRowClick={(r) => setOpenId(r.id)}
        emptyIcon="⛉"
        emptyTitle="Nobody here"
        toolbar={
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <Select value={sector} onChange={(e) => set('sector', e.target.value)} options={SECTORS} />
            <Select
              value={status}
              onChange={(e) => set('status', e.target.value)}
              options={[{ value: '', label: 'Any status' }, ...Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label }))]}
            />
            <Chips
              value={deadline}
              onChange={(v) => set('deadline', v)}
              options={[
                { value: '', label: 'Any deadline' },
                { value: 'soon', label: 'Due within 7 days' },
                { value: 'expired', label: 'Expired' },
              ]}
            />
            <SearchInput value={q} onChange={(v) => set('q', v)} placeholder="Name or mobile" />
          </div>
        }
      />

      {openId && <ApplicationModal id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function ApplicationModal({ id, onClose }: { id: string; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const detail = useQuery({ queryKey: ['kyc-management', id], queryFn: () => api.get<Detail>(`/admin/kyc-management/${id}`) });
  const d = detail.data;

  const decide = useMutation({
    mutationFn: (action: 'approve' | 'reject' | 'request-changes') => api.post(`/admin/kyc-management/${id}/${action}`, { note: note || undefined }),
    onSuccess: (_, action) => {
      toast(action === 'approve' ? 'Approved' : action === 'reject' ? 'Rejected' : 'Changes requested', 'success');
      qc.invalidateQueries({ queryKey: ['kyc-management'] });
      qc.invalidateQueries({ queryKey: ['kyc-summary'] });
      onClose();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  return (
    <Modal
      title={d ? `${d.user.displayName ?? 'Unnamed'} · ${sectorName(d.sector)}` : 'Application'}
      sub={d ? STATUS_LABEL[d.status] : undefined}
      onClose={onClose}
      wide
      footer={
        d && (
          <>
            <Button variant="danger" loading={decide.isPending} onClick={() => decide.mutate('reject')} disabled={note.trim().length < 3}>
              Reject
            </Button>
            <Button loading={decide.isPending} onClick={() => decide.mutate('request-changes')} disabled={note.trim().length < 3}>
              Request changes
            </Button>
            <Button variant="success" loading={decide.isPending} onClick={() => decide.mutate('approve')} disabled={d.status === 'VERIFIED'}>
              Approve
            </Button>
          </>
        )
      }
    >
      {!d ? (
        <div className="skeleton" style={{ height: 240 }} />
      ) : (
        <div className="stack">
          <Card>
            <CardHead title="Person" />
            <div className="kv" style={{ paddingTop: 'var(--s3)' }}>
              <span className="kv__k">Name</span>
              <span className="kv__v">{d.user.displayName ?? '—'}</span>
              <span className="kv__k">Verified mobile</span>
              <span className="kv__v cell-mono">{d.user.mobile}</span>
              <span className="kv__k">Identity KYC</span>
              <span className="kv__v">{humanise(d.user.kycStatus)}</span>
              {d.rule === 'GRACE' && (
                <>
                  <span className="kv__k">First used section</span>
                  <span className="kv__v">{dateTime(d.activatedAt)}</span>
                  <span className="kv__k">KYC due</span>
                  <span className="kv__v">{dateTime(d.graceEndsAt)} {d.daysLeft !== null && d.status !== 'VERIFIED' ? `(${d.daysLeft} days left)` : ''}</span>
                </>
              )}
              <span className="kv__k">Listings in section</span>
              <span className="kv__v">
                {Object.keys(d.listings).length ? Object.entries(d.listings).map(([k, v]) => `${humanise(k)}: ${v}`).join(' · ') : 'None'}
              </span>
              {d.decisionNote && (
                <>
                  <span className="kv__k">Last note</span>
                  <span className="kv__v">{d.decisionNote}</span>
                </>
              )}
            </div>
          </Card>

          <Card>
            <CardHead title="Identity checks (DigiLocker)" />
            {d.identity.length ? (
              <table className="data">
                <tbody>
                  {d.identity.map((r) => (
                    <tr key={r.id}>
                      <td>{humanise(r.docType)} {r.docLast4 ? `••${r.docLast4}` : ''}</td>
                      <td>{r.nameOnDoc ?? '—'}</td>
                      <td>{humanise(r.status)}</td>
                      <td className="cell-muted">{dateTime(r.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="cell-muted">No identity check started yet.</p>
            )}
          </Card>

          {d.provider && (
            <Card>
              <CardHead title="Service details" sub={d.provider.isLive ? 'Live now' : 'Not live'} />
              <div className="kv" style={{ paddingTop: 'var(--s3)' }}>
                <span className="kv__k">Service</span>
                <span className="kv__v">{d.provider.customService ?? d.provider.category.name}</span>
                <span className="kv__k">Business name</span>
                <span className="kv__v">{d.provider.businessName ?? '—'}</span>
                <span className="kv__k">Provider name</span>
                <span className="kv__v">{d.provider.name}</span>
                <span className="kv__k">WhatsApp</span>
                <span className="kv__v cell-mono">{d.provider.whatsappMobile ?? '—'}</span>
                <span className="kv__k">Address</span>
                <span className="kv__v">{d.provider.address}</span>
                <span className="kv__k">Areas served</span>
                <span className="kv__v">{d.provider.coverageArea ?? '—'}</span>
                <span className="kv__k">Experience</span>
                <span className="kv__v">{d.provider.experienceYears != null ? `${d.provider.experienceYears} years` : '—'}</span>
                <span className="kv__k">Qualifications</span>
                <span className="kv__v">{d.provider.qualifications ?? '—'}</span>
                <span className="kv__k">Pricing</span>
                <span className="kv__v">{d.provider.pricing ?? '—'}</span>
                <span className="kv__k">Team size</span>
                <span className="kv__v">{d.provider.teamSize}</span>
                <span className="kv__k">Description</span>
                <span className="kv__v">{d.provider.workDescription}</span>
              </div>
              {d.provider.workMedia.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 'var(--s3)' }}>
                  {d.provider.workMedia.map((u) => (
                    <a key={u} href={u} target="_blank" rel="noreferrer">
                      <img src={u} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8 }} />
                    </a>
                  ))}
                </div>
              )}
            </Card>
          )}

          <Card>
            <CardHead title="Documents" sub="Private. Each link works for 5 minutes and is opened only by you." />
            {d.documents.length ? (
              <table className="data">
                <tbody>
                  {d.documents.map((doc) => (
                    <tr key={doc.id}>
                      <td>{humanise(doc.kind)}</td>
                      <td>{doc.fileName}</td>
                      <td className="cell-muted">{dateTime(doc.createdAt)}</td>
                      <td>
                        <a href={doc.url} target="_blank" rel="noreferrer">Open</a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="cell-muted">No documents uploaded.</p>
            )}
          </Card>

          <Card>
            <CardHead title="Decision" />
            <Field label="Note" hint="Required to reject or to ask for changes. The person sees it.">
              <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Upload your trade licence" />
            </Field>
          </Card>

          <Card>
            <CardHead title="History" />
            {d.history.length ? (
              <table className="data">
                <tbody>
                  {d.history.map((h) => (
                    <tr key={h.id}>
                      <td className="cell-muted">{dateTime(h.createdAt)}</td>
                      <td className="cell-strong">{humanise(h.action.replace(/^KYC_ACCESS_/, ''))}</td>
                      <td>{h.actor?.displayName ?? 'System'}</td>
                      <td className="cell-muted">{h.after?.note ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="cell-muted">Nothing recorded yet.</p>
            )}
          </Card>
        </div>
      )}
    </Modal>
  );
}
